import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    TextField,
    Autocomplete,
    MenuItem,
    Stack,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Alert,
    AlertTitle,
    Box,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { MdAdd, MdDelete } from "react-icons/md";
import EnhancedAxios from "../../axios/EnhancedAxios";
import { useSelector } from "react-redux";
import { AxiosResponse, AxiosError } from "axios";
import { successAlert, errorAlert } from "../../utils/alerts/alerts";
import { giftSchema, updateGiftSchema } from "../../schemes/gift/giftSchema";
import SelectCategory from "../select/SelectCategory";
import DialogWrapper from "./DialogWrapper";
import UsernameSelectorModal from "./UsernameSelectorModal";
import PaginationFilter from "../filter/PaginationFilter";

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onGiftAdded?: () => void;
    gift?: any; // Gift data for edit mode
    isEdit?: boolean; // Flag to determine if we're in edit mode
};

type GiftFormData = {
    gift_type: { id: number; name: string } | null;
    compensate_type: string;
    valid_from: moment.Moment;
    valid_to: moment.Moment;
    days_amount?: number;
    traffic_amount?: number;
    note: string;
    comment: string;
    usernames: string[];
};

const AddGiftModal = ({ open, setOpen, onGiftAdded, gift, isEdit = false }: Props) => {
    const userToken = useSelector((state: any) => state.user.userToken);
    const [submitting, setSubmitting] = useState(false);
    const [usernames, setUsernames] = useState<string[]>([]);
    const [usernameInput, setUsernameInput] = useState("");
    const [selectorModalOpen, setSelectorModalOpen] = useState(false);
    const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
        {}
    );
    const [generalError, setGeneralError] = useState<string>("");
    const [loadingExistingUsernames, setLoadingExistingUsernames] = useState(false);
    const [currentUsernamePage, setCurrentUsernamePage] = useState(1);
    const usernameItemsPerPage = 10;
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const prevOpenRef = useRef<boolean>(false);

    // Scroll to top when server errors are displayed
    useEffect(() => {
        if (
            (Object.keys(serverErrors).length > 0 || generalError) &&
            dialogContentRef.current
        ) {
            dialogContentRef.current.scrollTop = 0;
        }
    }, [serverErrors, generalError]);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
    } = useForm<any>({
        resolver: yupResolver(isEdit ? updateGiftSchema : giftSchema),
        defaultValues: {
            gift_type: gift?.gift_type || null,
            compensate_type: gift?.compensate_type ? (Array.isArray(gift.compensate_type) ? (gift.compensate_type.includes("both") ? "both" : gift.compensate_type[0]) : gift.compensate_type) : "",
            valid_from: gift?.valid_from ? moment(gift.valid_from) : moment(),
            valid_to: gift?.valid_to ? moment(gift.valid_to) : moment(),
            days_amount: gift?.days_amount || undefined,
            traffic_amount: gift?.traffic_amount || undefined,
            note: gift?.note || "",
            comment: gift?.comment || gift?.notification_body || "",
            usernames: gift?.usernames || [],
        },
    });

    // Fetch existing usernames from API in edit mode
    useEffect(() => {
        if (open && isEdit && gift?.id) {
            setLoadingExistingUsernames(true);
            setCurrentUsernamePage(1);
            EnhancedAxios(null, userToken)
                .get(`/admin/gifts/${gift.id}/usernames?page=1&page_size=999999`)
                .then((res: AxiosResponse) => {
                    const fetchedUsernames = res.data.usernames || [];
                    setUsernames(fetchedUsernames);
                    setValue("usernames", fetchedUsernames);
                    setLoadingExistingUsernames(false);
                })
                .catch((error: AxiosError) => {
                    errorAlert(error);
                    setLoadingExistingUsernames(false);
                });
        } else if (!open && isEdit) {
            // Reset loading state when modal closes in edit mode
            setLoadingExistingUsernames(false);
            setCurrentUsernamePage(1);
        }
    }, [open, isEdit, gift?.id, userToken, setValue]);

    // Update form when modal opens (for edit mode) - only initialize when transitioning from closed to open
    useEffect(() => {
        const isOpening = open && !prevOpenRef.current;
        prevOpenRef.current = open;

        if (isOpening && isEdit && gift) {
            const compensateType = gift.compensate_type
                ? (Array.isArray(gift.compensate_type)
                    ? (gift.compensate_type.includes("both") ? "both" : gift.compensate_type[0])
                    : gift.compensate_type)
                : "";

            setValue("gift_type", gift.gift_type || null);
            setValue("compensate_type", compensateType);
            setValue("valid_from", gift.valid_from ? moment(gift.valid_from) : moment());
            setValue("valid_to", gift.valid_to ? moment(gift.valid_to) : moment());

            // Handle amount fields - check for amount_days/amount_mb first, fallback to amount if needed
            if (gift.days_amount !== undefined) {
                setValue("days_amount", gift.days_amount);
            } else if (gift.days_amount && (compensateType === "days" || compensateType === "both")) {
                setValue("days_amount", gift.amount);
            } else {
                setValue("days_amount", undefined);
            }

            if (gift.traffic_amount !== undefined) {
                setValue("traffic_amount", gift.traffic_amount);
            } else if (gift.traffic_amount && (compensateType === "traffic" || compensateType === "both")) {
                setValue("traffic_amount", gift.traffic_amount);
            } else {
                setValue("traffic_amount", undefined);
            }

            setValue("note", gift.note || "");
            setValue("comment", gift.comment || gift.notification_body || "");
        }
    }, [open, isEdit, gift, setValue]);

    const handleClose = () => {
        setOpen(false);
        reset();
        setUsernames([]);
        setUsernameInput("");
        setSelectorModalOpen(false);
        setServerErrors({});
        setGeneralError("");
        setCurrentUsernamePage(1);
    };

    const onSubmit = async (data: GiftFormData) => {
        setSubmitting(true);
        setServerErrors({}); // Clear previous server errors
        setGeneralError(""); // Clear previous general errors
        try {
            const giftData = {
                ...data,
                usernames: usernames,
                valid_from: moment(data.valid_from)
                    .utc()
                    .startOf("day")
                    .toISOString(),
                valid_to: moment(data.valid_to)
                    .utc()
                    .startOf("day")
                    .toISOString(),
            };

            if (isEdit && gift?.id) {
                // Update existing gift
                await EnhancedAxios(null, userToken).put(`/admin/gifts/${gift.id}`, giftData);
                successAlert("تم تحديث الهدية بنجاح");
            } else {
                // Create new gift
                await EnhancedAxios(null, userToken).post("/admin/gifts", giftData);
                successAlert("تم إضافة الهدية بنجاح");
            }
            handleClose();
            onGiftAdded?.();
        } catch (error: any) {
            if (error.response?.status === 422) {
                // Handle validation errors from server
                const validationErrors = error.response?.data?.errors || {};
                setServerErrors(validationErrors);
            } else {
                // Show specific error message at top of modal
                const errorMessage =
                    error.response?.data?.message ||
                    error.message ||
                    (isEdit ? "حدث خطأ أثناء تحديث الهدية" : "حدث خطأ أثناء إضافة الهدية");
                setGeneralError(errorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddUsername = () => {
        if (usernameInput.trim() && !usernames.includes(usernameInput.trim())) {
            const newUsernames = [...usernames, usernameInput.trim()];
            setUsernames(newUsernames);
            setValue("usernames", newUsernames);
            setUsernameInput("");
        }
    };

    const handleRemoveUsername = (usernameToRemove: string) => {
        const newUsernames = usernames.filter(
            (username) => username !== usernameToRemove
        );
        setUsernames(newUsernames);
        setValue("usernames", newUsernames);
        // Reset to page 1 if current page becomes empty
        const totalPages = Math.ceil(newUsernames.length / usernameItemsPerPage);
        if (currentUsernamePage > totalPages && totalPages > 0) {
            setCurrentUsernamePage(totalPages);
        }
    };

    const handleUsernamesSelected = (selectedUsernames: string[]) => {
        // Filter out duplicates and add only new usernames
        const uniqueNewUsernames = selectedUsernames.filter(
            (username) => !usernames.includes(username)
        );
        const updatedUsernames = [...usernames, ...uniqueNewUsernames];
        setUsernames(updatedUsernames);
        setValue("usernames", updatedUsernames);
        // Navigate to the last page where new usernames are added
        const totalPages = Math.ceil(updatedUsernames.length / usernameItemsPerPage);
        setCurrentUsernamePage(totalPages);
    };

    const handleSetOpen = (
        value: boolean | ((prevState: boolean) => boolean)
    ) => {
        if (typeof value === "function") {
            setOpen(value(open));
        } else {
            setOpen(value);
        }
    };

    return (
        <DialogWrapper open={open} setOpen={handleSetOpen}>
            <DialogTitle>{isEdit ? "تعديل الهدية" : "إضافة هدية جديدة"}</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent
                    ref={dialogContentRef}
                    sx={{ overflowY: "auto", maxHeight: "70vh" }}
                >
                    {/* General Error Display */}
                    {generalError && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                minWidth: "200px !important",
                                maxWidth: "100%",
                            }}
                        >
                            {generalError}
                        </Alert>
                    )}

                    {/* Server Validation Errors Display */}
                    {Object.keys(serverErrors).length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle>خطأ في التحقق من البيانات</AlertTitle>
                            <Box>
                                {Object.entries(serverErrors).map(
                                    ([field, errors]) => (
                                        <Box key={field} sx={{ mb: 1 }}>
                                            <strong>{field}:</strong>
                                            <ul
                                                style={{
                                                    margin: 0,
                                                    paddingLeft: 20,
                                                }}
                                            >
                                                {errors.map((error, index) => (
                                                    <li key={index}>{error}</li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )
                                )}
                            </Box>
                        </Alert>
                    )}

                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {/* Gift Type */}
                        <SelectCategory
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            name="gift_type"
                            multiple={false}
                            categoriesParent={
                                process.env.REACT_APP_GIFT_TYPE_CAT_ID
                            }
                            errorMessage={errors?.gift_type?.message as string}
                        />

                        {/* Compensate Type */}
                        <Controller
                            name="compensate_type"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value || ""}
                                    select
                                    onChange={(e) => {
                                        const value = e.target.value as string;
                                        field.onChange(value);
                                        // Set the other type's amount to 0 when type changes
                                        if (value === "days") {
                                            setValue("traffic_amount", 0);
                                        } else if (value === "traffic") {
                                            setValue("days_amount", 0);
                                        } else if (value === "both") {
                                            // Keep both values when "both" is selected
                                        }
                                    }}
                                    label="النوع "
                                    error={!!errors.compensate_type}
                                    helperText={
                                        errors.compensate_type
                                            ?.message as string
                                    }
                                    fullWidth
                                >
                                    <MenuItem value="traffic">بيانات</MenuItem>
                                    <MenuItem value="days">ايام</MenuItem>
                                    <MenuItem value="both">كلاهما</MenuItem>
                                </TextField>
                            )}
                        />

                        {/* Valid From Date */}
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Controller
                                name="valid_from"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        label="تاريخ البداية"
                                        slotProps={{
                                            textField: {
                                                error: !!errors.valid_from,
                                                helperText: errors.valid_from
                                                    ?.message as string,
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>

                        {/* Valid To Date */}
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Controller
                                name="valid_to"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        label="تاريخ النهاية"
                                        slotProps={{
                                            textField: {
                                                error: !!errors.valid_to,
                                                helperText: errors.valid_to
                                                    ?.message as string,
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>

                        {/* Amount Days - Show only if days or both is selected */}
                        {(watch("compensate_type") === "days" || watch("compensate_type") === "both") && (
                            <Controller
                                name="days_amount"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        label="عدد الأيام"
                                        error={!!errors.days_amount}
                                        helperText={
                                            errors.days_amount
                                                ?.message as string
                                        }
                                        fullWidth
                                        inputProps={{ min: 0, step: 1 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    يوم
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}

                        {/* Amount MB - Show only if traffic or both is selected */}
                        {(watch("compensate_type") === "traffic" || watch("compensate_type") === "both") && (
                            <Controller
                                name="traffic_amount"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        label="قيمة البيانات"
                                        error={!!errors.traffic_amount}
                                        helperText={
                                            errors.traffic_amount?.message as string
                                        }
                                        fullWidth
                                        inputProps={{ min: 0, step: 1 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    MB
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}

                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="textarea"
                                    label="الملاحظات"
                                    error={!!errors.note}
                                    helperText={
                                        errors.note?.message as string
                                    }
                                    fullWidth
                                    rows={2}
                                    multiline={true}
                                    minRows={2}
                                    maxRows={5}
                                />
                            )}
                        />

                        {/* Comment */}
                        <Controller
                            name="comment"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="نص الاشعار"
                                    error={!!errors.comment}
                                    helperText={
                                        errors.comment
                                            ?.message as string
                                    }
                                    fullWidth
                                    rows={3}
                                    multiline={true}
                                    minRows={3}
                                    maxRows={6}
                                />
                            )}
                        />

                        {/* Usernames */}
                        <Stack spacing={2}>
                            {isEdit && loadingExistingUsernames ? (
                                <Box sx={{ textAlign: "center", py: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        جاري تحميل المستخدمين...
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {!isEdit && (
                                        <>
                                            <TextField
                                                label="اسم المستخدم"
                                                value={usernameInput}
                                                onChange={(e) =>
                                                    setUsernameInput(e.target.value)
                                                }
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleAddUsername();
                                                    }
                                                }}
                                                fullWidth
                                            />
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<MdAdd />}
                                                    onClick={handleAddUsername}
                                                    disabled={!usernameInput.trim()}
                                                    sx={{ flex: 1 }}
                                                >
                                                    إضافة مستخدم
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setSelectorModalOpen(true)}
                                                    sx={{ flex: 1 }}
                                                    type="button"
                                                >
                                                    اختيار من النظام
                                                </Button>
                                            </Stack>
                                        </>
                                    )}

                                    {isEdit && (
                                        <Button
                                            variant="contained"
                                            onClick={() => setSelectorModalOpen(true)}
                                            type="button"
                                            fullWidth
                                        >
                                            إضافة مستخدمين من النظام
                                        </Button>
                                    )}

                                    {usernames.length > 0 && (() => {
                                        // Calculate pagination
                                        const startIndex = (currentUsernamePage - 1) * usernameItemsPerPage;
                                        const endIndex = startIndex + usernameItemsPerPage;
                                        const paginatedUsernames = usernames.slice(startIndex, endIndex);

                                        return (
                                            <Stack spacing={1}>
                                                <label>
                                                    {isEdit ? "المستخدمون الحاليون:" : "المستخدمون المضافون:"} ({usernames.length})
                                                </label>
                                                {paginatedUsernames.map((username, index) => (
                                                    <Stack
                                                        key={`${username}-${startIndex + index}`}
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <TextField
                                                            value={username}
                                                            size="small"
                                                            disabled
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() =>
                                                                handleRemoveUsername(
                                                                    username
                                                                )
                                                            }
                                                        >
                                                            <MdDelete />
                                                        </IconButton>
                                                    </Stack>
                                                ))}
                                                {usernames.length > usernameItemsPerPage && (
                                                    <PaginationFilter
                                                        totalItems={usernames.length}
                                                        itemsPerPage={usernameItemsPerPage}
                                                        currentPage={currentUsernamePage}
                                                        setCurrentPage={setCurrentUsernamePage}
                                                    />
                                                )}
                                            </Stack>
                                        );
                                    })()}
                                    {errors.usernames && (
                                        <span
                                            style={{
                                                color: "red",
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            {errors.usernames.message
                                                ? String(errors.usernames.message)
                                                : ""}
                                        </span>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>
                        إلغاء
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting
                            ? (isEdit ? "جاري التحديث..." : "جاري الإضافة...")
                            : (isEdit ? "تحديث الهدية" : "إضافة الهدية")}
                    </Button>
                </DialogActions>
            </form>
            <UsernameSelectorModal
                open={selectorModalOpen}
                setOpen={setSelectorModalOpen}
                onUsernamesSelected={handleUsernamesSelected}
                existingUsernames={usernames}
            />
        </DialogWrapper>
    );
};

export default AddGiftModal;
